/**
 * Budget versus actual, and the scenarios built on top (KAN-56 / KAN-57 /
 * KAN-93 / KAN-94).
 *
 * The sign convention is the part worth reading twice. Over budget on an
 * expense is bad; over budget on revenue is good. Getting that wrong does not
 * produce a subtly imperfect report — it produces one whose colours actively
 * mislead, which is worse than no colours at all. `overIsFavourable` in
 * `domain/gl.ts` is the single place that decision is made, and everything here
 * reads it.
 */
/* ------------------------------------------------------------------------ *
 * Periods
 * ------------------------------------------------------------------------ */
/**
 * The period shapes a comparison is offered over.
 *
 * `ytd` is separate from `annual` rather than being "annual, truncated": a
 * year-to-date actual compared against a full-year budget is the single most
 * common way a budget report is misread — every line looks wildly under — and
 * naming the two differently is what lets the comparison prorate the budget and
 * say that it did.
 */
export const BUDGET_VIEWS = ['monthly', 'quarterly', 'ytd', 'annual'];
export const BUDGET_VIEW_LABELS = {
    monthly: 'Month',
    quarterly: 'Quarter',
    ytd: 'Year to date',
    annual: 'Full year',
};
/**
 * Which accounting basis the actuals were read on.
 *
 * Not defaulted anywhere in this codebase. Cash and accrual give materially
 * different answers for the same month, and which one the firm budgets on is
 * Finance's convention rather than a technical choice — so the caller states it
 * and the screen prints it beside the figures.
 */
export const ACCOUNTING_METHODS = ['Cash', 'Accrual'];
/* ------------------------------------------------------------------------ *
 * Variance
 * ------------------------------------------------------------------------ */
/**
 * How a line's variance should be read.
 *
 * `no_budget` is the state KAN-93 is most insistent about: a category with
 * actuals and no budget must read "no budget set", never "100% over" and never
 * "0% variance". A fabricated variance is worse than an admitted gap, because
 * somebody acts on it.
 *
 * `no_actual` is its mirror and matters nearly as much — a budgeted line with
 * nothing spent against it is either a saving or a system that is not reporting,
 * and the reader has to be the one who decides which.
 */
export const VARIANCE_STATES = [
    'on_budget',
    'favourable',
    'unfavourable',
    'no_budget',
    'no_actual',
    'undirected',
];
export const VARIANCE_STATE_LABELS = {
    on_budget: 'On budget',
    favourable: 'Favourable',
    unfavourable: 'Unfavourable',
    no_budget: 'No budget set',
    no_actual: 'No activity',
    undirected: 'No direction',
};
/**
 * How far a line may sit from budget before it stops counting as on budget, in
 * basis points.
 *
 * 200 — two per cent. Not zero, because no account lands exactly on its budget
 * and a report where every line is flagged is a report nobody reads. Not larger,
 * because this is a threshold for *colour*, not for attention: the amount and
 * the percentage are both on the row regardless.
 */
export const BUDGET_ON_TARGET_TOLERANCE_BPS = 200;
/* ------------------------------------------------------------------------ *
 * Scenarios
 * ------------------------------------------------------------------------ */
export const SCENARIO_STATUSES = ['draft', 'active', 'archived'];
export const SCENARIO_STATUS_LABELS = {
    draft: 'Draft',
    active: 'Active',
    archived: 'Archived',
};
export const ADJUSTMENT_KINDS = ['amount', 'rate'];
/** KAN-94 asks for two to three side by side. */
export const MAX_SCENARIOS_COMPARED = 3;
//# sourceMappingURL=budget.js.map